import type { YomiriManga } from '../../types/extension.ts';
import { useEffect, useState, useMemo } from 'react';
import { Play, Bookmark, BookmarkCheck, Bell, Settings2, DownloadCloud, ChevronDown, CheckCheck, Clock, Pause, XCircle } from 'lucide-react';
import { discoveryStatuses, readDiscoveryStatus, setDiscoveryStatus, discoveryNotice, type DiscoveryStatus } from '../../metadata/DiscoveryCollection.ts';
import { readDiscoveryAlerts, toggleDiscoveryAlert } from '../../metadata/DiscoveryAlerts.ts';
import { type HomeMedia } from '../../metadata/HomeRecommendations.ts';
import { YomiriActionMenu, type YomiriActionMenuOption } from './common/YomiriActionMenu.tsx';

export function DiscoveryActions({
  media,
  onPlay,
  onBulk,
  onSources,
  manga,
}: {
  manga?: YomiriManga;
  media: HomeMedia;
  onPlay: () => void;
  onBulk: () => void;
  onSources: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<DiscoveryStatus | null>(null);
  const [alert, setAlert] = useState(false);

  useEffect(() => {
    let live = true;
    const refresh = () => {
      void readDiscoveryStatus(media, manga).then((value) => {
        if (live) setStatus(value);
      }).catch(() => {});
      void readDiscoveryAlerts().then((rows) => {
        if (live) setAlert(rows.some((row) => row.id === media.id));
      }).catch(() => {});
    };
    refresh();
    window.addEventListener('yomiri-library-changed', refresh);
    return () => {
      live = false;
      window.removeEventListener('yomiri-library-changed', refresh);
    };
  }, [media.id, manga?.id, manga?.sourceId]);

  const choose = (value: DiscoveryStatus | null) => {
    setAnchor(null);
    void setDiscoveryStatus(media, value, manga)
      .then(() => setStatus(value))
      .catch(() => discoveryNotice('No se pudo actualizar tu lista.'));
  };

  const statusIcons: Record<string, React.ReactNode> = {
    CURRENT: <Play size={14} />,
    PLANNING: <Clock size={14} />,
    COMPLETED: <CheckCheck size={14} />,
    PAUSED: <Pause size={14} />,
    DROPPED: <XCircle size={14} />,
  };

  const statusColors: Record<string, string> = {
    CURRENT: '#ff2e63',
    PLANNING: '#38bdf8',
    COMPLETED: '#4ade80',
    PAUSED: '#fbbf24',
    DROPPED: '#f87171',
  };

  const menuOptions = useMemo<YomiriActionMenuOption[]>(() => {
    return discoveryStatuses.map((row) => ({
      value: row.value,
      label: media.type === 'MANGA' && row.value === 'CURRENT' ? 'Leyendo' : row.label,
      icon: statusIcons[row.value],
      color: statusColors[row.value],
    }));
  }, [media.type]);

  const currentLabel = status
    ? media.type === 'MANGA' && status === 'CURRENT'
      ? 'Leyendo'
      : discoveryStatuses.find((row) => row.value === status)?.label
    : 'Añadir a la lista';

  return (
    <>
      <div className="nyomiri-detail-actions">
        <button
          className="nyomiri-btn nyomiri-btn-primary"
          disabled={media.status === 'NOT_YET_RELEASED'}
          onClick={onPlay}
        >
          <Play size={16} />
          {media.type === 'MANGA' ? 'Leer ahora' : 'Ver el episodio 1'}
        </button>

        <button
          className="nyomiri-btn nyomiri-btn-neutral"
          aria-haspopup="menu"
          aria-expanded={!!anchor}
          aria-label={status ? `En tu lista: ${currentLabel}` : 'Añadir a la lista'}
          data-saved={!!status}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          {status ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
          <span>{currentLabel}</span>
          <ChevronDown size={14} />
        </button>

        {media.type === 'ANIME' && (
          <button
            className="nyomiri-btn nyomiri-btn-neutral"
            aria-pressed={alert}
            title="Aviso de emisión mientras Yomiri está abierto"
            disabled={!media.nextAiringEpisode}
            onClick={() =>
              void toggleDiscoveryAlert(media)
                .then((value) => {
                  setAlert(value);
                  discoveryNotice(value ? 'Aviso activado mientras Yomiri esté abierto.' : 'Aviso desactivado.');
                })
                .catch((cause) => discoveryNotice(cause.message))
            }
          >
            <Bell size={16} />
            {alert ? 'Aviso activado' : 'Avisarme'}
          </button>
        )}

        <button className="nyomiri-btn nyomiri-btn-neutral" onClick={onSources}>
          <Settings2 size={16} />
          Configuración de fuentes
        </button>

        <button className="nyomiri-btn nyomiri-btn-neutral" onClick={onBulk}>
          <DownloadCloud size={16} />
          Descargar
        </button>
      </div>

      <YomiriActionMenu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        title="Añadir a tu biblioteca"
        options={menuOptions}
        selectedValue={status}
        onSelect={(val) => choose(val as DiscoveryStatus)}
        allowClear={!!status}
        clearLabel="Quitar de la lista"
        onClear={() => choose(null)}
      />
    </>
  );
}
